import adminHttp from './adminHttp';

/**
 * Query-key factory + one function per endpoint, mirroring
 * modules/PermissionsAdmin/api/permissionsApi.js.
 *
 * `all` is the invalidation root: every mutation invalidates it, so a create,
 * edit, delete or plan change refreshes whatever list is on screen without the
 * caller knowing which filters produced it.
 */
export const adminKeys = {
  all: ['adminOrganizations'],
  list: (params) => ['adminOrganizations', 'list', params ?? {}],
  plans: (type) => ['adminPlans', type ?? null],
  instances: ['adminInstances'],
  erpDemand: ['adminErpDemand', 'summary'],
  erpRequests: (params) => ['adminErpDemand', 'requests', params ?? {}],
};

/**
 * GET /admin/organizations
 *
 * `params` is passed through as query params. Empty values are dropped rather
 * than sent as `?name=`: the backend treats an empty string as a filter and would
 * match everything containing "" — harmless — but it also lands in the URL and in
 * the query key, so two states that mean "no name filter" would cache separately.
 */
export const fetchOrganizations = async (params = {}) => {
  const query = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== '' && v !== null && v !== undefined),
  );
  const { data } = await adminHttp.get('/admin/organizations', { params: query });
  return data;
};

export const createOrganization = async (payload) => {
  const { data } = await adminHttp.post('/admin/organizations', payload);
  return data;
};

export const updateOrganization = async (id, payload) => {
  const { data } = await adminHttp.patch(`/admin/organizations/${id}`, payload);
  return data;
};

export const deleteOrganization = async (id) => {
  const { data } = await adminHttp.delete(`/admin/organizations/${id}`);
  return data;
};

export const updateOrganizationPlan = async (id, payload) => {
  const { data } = await adminHttp.patch(`/admin/organizations/${id}/plan`, payload);
  return data;
};

/**
 * Verifier firm administrators (verifier-firm-foundation phase 2, R8 mitigation): a firm's first
 * Admin is whoever used its first invite; CG staff can reassign.
 */
export const fetchVerifierMembers = async (id) => {
  const { data } = await adminHttp.get(`/admin/organizations/${id}/verifier-members`);
  return data.members;
};

export const setVerifierMemberRole = async ({ id, clerkUserId, role }) => {
  const { data } = await adminHttp.patch(
    `/admin/organizations/${id}/verifier-members/${encodeURIComponent(clerkUserId)}`,
    { role },
  );
  return data;
};
